import axios, { AxiosResponse } from 'axios';
import {
    CompanyInfo,
    Client,
    CreateClientResponse,
    GetClientsResponse,
    GetClientResponse,
    UploadFileResponse,
    ApiResponse,
    CategoryWeights,
    CategoryWeightsResponse,
    RecalculateClientResponse,
    LoginRequest,
    LoginResponse,
    UserInfoResponse,
    UserSettingsResponse,
    UpdateSettingsRequest,
    EquipmentResponse,
    LoansResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds timeout
    headers: {
        'Content-Type': 'application/json',
    },
});

// Token management
const TOKEN_KEY = 'auth_token';

export const setAuthToken = (token: string) => {
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const getAuthToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
};

export const removeAuthToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common['Authorization'];
};

// Initialize token if it exists
const token = getAuthToken();
if (token) {
    setAuthToken(token);
}

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401 && getAuthToken()) {
            removeAuthToken();
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

// Handle API errors consistently
const handleApiError = (error: any): never => {
    console.error('API Error:', error);

    if (error.response) {
        throw new Error(`${error.response.data?.error}: ${error.response.data?.message}`);
    } else if (error.request) {
        throw new Error('Unable to connect to the server. Please check if the backend is running.');
    } else {
        throw new Error(error.message || 'An unexpected error occurred');
    }
};

export const getHealthCheck = async (): Promise<ApiResponse> => {
    try {
        const response: AxiosResponse<ApiResponse> = await api.get('/health');
        return response.data;
    } catch (error) {
        throw new Error('Backend service is not available');
    }
};

// Authentication API Functions

export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
        const response: AxiosResponse<LoginResponse> = await api.post('/auth/login', credentials);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        // Set token for future requests
        if (response.data.token) {
            setAuthToken(response.data.token);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const logout = () => {
    removeAuthToken();
};

export const getCurrentUser = async (): Promise<UserInfoResponse> => {
    try {
        const response: AxiosResponse<UserInfoResponse> = await api.get('/auth/me');

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getUserSettings = async (): Promise<UserSettingsResponse> => {
    try {
        const response: AxiosResponse<UserSettingsResponse> = await api.get('/auth/settings');

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const updateUserSettings = async (settings: UpdateSettingsRequest): Promise<UserSettingsResponse> => {
    try {
        const response: AxiosResponse<UserSettingsResponse> = await api.put('/auth/settings', settings);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

// Client Management API Functions

export const createClient = async (companyInfo: CompanyInfo): Promise<CreateClientResponse> => {
    try {
        const clientData = {
            company_name: companyInfo.companyName,
            industry: companyInfo.industry,
            fiscal_year: companyInfo.fiscalYear,
            currency: companyInfo.currency || 'USD',
            reporting_period: companyInfo.reportingPeriod || 'annual',
            scale: companyInfo.scale || 'thousands'
        };

        const response: AxiosResponse<CreateClientResponse> = await api.post('/clients', clientData);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getAllClients = async (page: number = 1, perPage: number = 20): Promise<GetClientsResponse> => {
    try {
        const response: AxiosResponse<GetClientsResponse> = await api.get(`/clients?page=${page}&per_page=${perPage}`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getClient = async (clientId: string): Promise<GetClientResponse> => {
    try {
        const response: AxiosResponse<GetClientResponse> = await api.get(`/clients/${clientId}`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const deleteClient = async (clientId: string): Promise<ApiResponse> => {
    try {
        const response: AxiosResponse<ApiResponse> = await api.delete(`/clients/${clientId}`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const uploadFileToClient = async (
    clientId: string,
    fileName: string,
    fileContent: string,
    fileCategory?: string
): Promise<UploadFileResponse> => {
    try {
        const uploadData = {
            file_name: fileName,
            file_content: fileContent, // Send raw file content
            file_size: fileContent.length,
            ...(fileCategory && { file_category: fileCategory })
        };

        const response: AxiosResponse<UploadFileResponse> = await api.post(`/clients/${clientId}/files`, uploadData);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const evaluateCreditWithId = async (clientId: string): Promise<Client> => {
    try {
        const response: AxiosResponse<Client> = await api.post('/evaluate', { client_id: clientId });

        if ((response.data as any).error) {
            throw new Error((response.data as any).error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getDefaultCategoryWeights = async (): Promise<CategoryWeightsResponse> => {
    try {
        const response: AxiosResponse<CategoryWeightsResponse> = await api.get('/category-weights');

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getFieldCategories = async (): Promise<ApiResponse<Record<string, string[]>>> => {
    try {
        const response: AxiosResponse<ApiResponse<Record<string, string[]>>> = await api.get('/field-categories');

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const recalculateClient = async (
    clientId: string,
    categoryWeights: CategoryWeights
): Promise<RecalculateClientResponse> => {
    try {
        const response: AxiosResponse<RecalculateClientResponse> = await api.post(
            `/clients/${clientId}/recalculate`,
            { category_weights: categoryWeights }
        );

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getClientTrends = async (clientId: string): Promise<ApiResponse<Array<{ year: number, ratios: Record<string, number> }>>> => {
    try {
        const response: AxiosResponse<ApiResponse<Array<{ year: number, ratios: Record<string, number> }>>> = await api.get(`/clients/${clientId}/trends`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const downloadExcelReport = async (clientId: string): Promise<void> => {
    try {
        const response = await api.get(`/clients/${clientId}/excel-report`, {
            responseType: 'blob',
        });

        // Create blob URL for file download
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        // Get filename from response headers or generate default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'Credit_Report.xlsx';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Error downloading Excel report:', error);
        throw error;
    }
};

export const downloadDocumentsPackage = async (clientId: string): Promise<void> => {
    try {
        const response = await api.get(`/clients/${clientId}/documents-package`, {
            responseType: 'blob',
        });

        // Create blob URL for file download
        const blob = new Blob([response.data], {
            type: 'application/zip'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        // Get filename from response headers or generate default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'Documents_Package.zip';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Error downloading documents package:', error);
        throw error;
    }
};

export const downloadClientFile = async (clientId: string, fileId: string): Promise<void> => {
    try {
        const response = await api.get(`/clients/${clientId}/files/${fileId}/download`, {
            responseType: 'blob',
        });

        // Create blob URL for file download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        // Get filename from response headers or generate default
        const contentDisposition = response.headers['content-disposition'];
        console.log('Content disposition:', response.headers);
        let filename = 'download';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        link.href = url;
        link.download = filename;
        console.log('Downloading file:', filename);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Error downloading file:', error);
        throw error;
    }
};

export const getClientEquipment = async (clientId: string): Promise<EquipmentResponse> => {
    try {
        const response: AxiosResponse<EquipmentResponse> = await api.get(`/clients/${clientId}/equipment`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getClientLoans = async (clientId: string): Promise<LoansResponse> => {
    try {
        const response: AxiosResponse<LoansResponse> = await api.get(`/clients/${clientId}/loans`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getUnassignedEquipment = async (): Promise<EquipmentResponse> => {
    try {
        const response: AxiosResponse<EquipmentResponse> = await api.get('/equipment/unassigned');

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const getClientDetails = async (clientId: string): Promise<ApiResponse<Record<string, any>>> => {
    try {
        const response: AxiosResponse<ApiResponse<Record<string, any>>> = await api.get(`/clients/${clientId}/details`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const updateClientDetails = async (
    clientId: string,
    details: Record<string, any>
): Promise<ApiResponse<Record<string, any>>> => {
    try {
        const response: AxiosResponse<ApiResponse<Record<string, any>>> = await api.put(
            `/clients/${clientId}/details`,
            { details }
        );

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export const nextStage = async (clientId: string): Promise<GetClientResponse> => {
    try {
        const response: AxiosResponse<GetClientResponse> = await api.post(`/clients/${clientId}/next_stage`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export interface ParsedDocument {
    file_id: string;
    file_name: string;
    parsed_data: any;
}

export interface ParsedDocumentsResponse extends ApiResponse {
    parsed_documents: {
        equifax: ParsedDocument[];
        paynet: ParsedDocument[];
        id: ParsedDocument[];
    };
}

export const getParsedDocuments = async (clientId: string): Promise<ParsedDocumentsResponse> => {
    try {
        const response: AxiosResponse<ParsedDocumentsResponse> = await api.get(`/clients/${clientId}/parsed-documents`);

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
};

export default api;
